import { useEffect, useState } from "react";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";

const emptyForm = {
  id: null,
  email: "",
  papel: 1,
  podeCriarLayouts: false,
  status: 0,
};

export default function UsuariosAdmin() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const editando = !!form.id;

  useEffect(() => {
    carregar();
  }, []);

  async function carregar() {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API}/usuario`);
      setUsuarios(res.data || []);
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", "Erro ao carregar usuários", { icon: "error" });
    } finally {
      setLoading(false);
    }
  }

  async function salvar(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      const payload = {
        papel: Number(form.papel),
        podeCriarLayouts: Number(form.papel) === 0 || !!form.podeCriarLayouts,
      };

      const res = editando
        ? await axios.put(`${process.env.REACT_APP_API}/usuario/${form.id}`, { ...payload, status: Number(form.status) })
        : await axios.post(`${process.env.REACT_APP_API}/usuario`, { ...payload, email: form.email });

      if (res.data?.linkAceite) {
        await navigator.clipboard?.writeText(res.data.linkAceite);
        AlertComponent("Convite criado", "Link de aceite copiado para a área de transferência", { icon: "success" });
      } else {
        AlertComponent("Sucesso", "Usuário salvo com sucesso", { icon: "success", timer: 1600 });
      }

      setForm(emptyForm);
      await carregar();
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao salvar usuário", { icon: "error" });
    } finally {
      setSalvando(false);
    }
  }

  function editar(usuario) {
    setForm({
      id: usuario.id,
      email: usuario.email,
      papel: usuario.papel,
      podeCriarLayouts: usuario.podeCriarLayouts,
      status: usuario.status,
    });
  }

  async function copiarLink(usuario) {
    if (!usuario.linkAceite) return;
    await navigator.clipboard?.writeText(usuario.linkAceite);
    AlertComponent("Copiado", "Link de aceite copiado", { icon: "success", timer: 1500 });
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Usuários</h1>
        <p className="text-sm text-slate-500 mt-1">
          Gerencie quem pode colaborar com os eventos deste organizador.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        <form onSubmit={salvar} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm h-fit">
          <h2 className="text-base font-semibold text-slate-800 mb-4">{editando ? "Editar usuário" : "Adicionar usuário"}</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">E-mail</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                disabled={editando || salvando}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 disabled:bg-slate-50"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Classificação neste organizador</label>
              <select
                value={form.papel}
                onChange={(e) => {
                  const papel = Number(e.target.value);
                  setForm((p) => ({ ...p, papel, podeCriarLayouts: papel === 0 ? true : p.podeCriarLayouts }));
                }}
                disabled={salvando}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
              >
                <option value={0}>Administrador</option>
                <option value={1}>Convidado</option>
              </select>
            </div>

            <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 text-sm">
              <input
                type="checkbox"
                checked={Number(form.papel) === 0 || form.podeCriarLayouts}
                onChange={(e) => setForm((p) => ({ ...p, podeCriarLayouts: e.target.checked }))}
                disabled={salvando || Number(form.papel) === 0}
                className="mt-0.5"
              />
              <span>
                <span className="block font-semibold text-slate-700">Pode criar layouts</span>
                <span className="block text-xs text-slate-500 mt-0.5">Administradores sempre possuem esta permissão.</span>
              </span>
            </label>

            {editando && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: Number(e.target.value) }))}
                  disabled={salvando}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                >
                  <option value={0}>Pendente</option>
                  <option value={1}>Aceito</option>
                  <option value={2}>Revogado</option>
                </select>
              </div>
            )}

            <div className="flex gap-2">
              <button disabled={salvando} className="flex-1 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {salvando ? "Salvando..." : editando ? "Salvar" : "Convidar"}
              </button>
              {editando && (
                <button type="button" onClick={() => setForm(emptyForm)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">
                  Cancelar
                </button>
              )}
            </div>
          </div>
        </form>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
            </div>
          ) : usuarios.length === 0 ? (
            <div className="text-center py-20 px-6 text-slate-500">
              Nenhum usuário vinculado a este organizador.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Usuário</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Papel</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Permissões</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {usuarios.map((usuario) => (
                    <tr key={usuario.id} className="hover:bg-slate-50">
                      <td className="px-5 py-4">
                        <p className="font-semibold text-slate-800">{obterNome(usuario)}</p>
                        <p className="text-sm text-slate-500">{usuario.email}</p>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-600">{usuario.papel === 0 ? "Administrador" : "Convidado"}</td>
                      <td className="px-4 py-4 text-sm text-slate-600">
                        {usuario.podeCriarLayouts ? "Cria layouts" : "Usa layouts existentes"}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${statusClass(usuario.status)}`}>
                          {statusLabel(usuario.status)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-center gap-2">
                          {usuario.linkAceite && (
                            <button onClick={() => copiarLink(usuario)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600">
                              Copiar link
                            </button>
                          )}
                          <button onClick={() => editar(usuario)} className="px-3 py-1.5 rounded-lg bg-slate-900 text-xs font-semibold text-white">
                            Editar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function obterNome(usuario) {
  const nome = [usuario.primeiroNome, usuario.ultimoNome].filter(Boolean).join(" ").trim();
  return nome || "Convite pendente";
}

function statusLabel(status) {
  if (status === 1) return "Aceito";
  if (status === 2) return "Revogado";
  return "Pendente";
}

function statusClass(status) {
  if (status === 1) return "bg-emerald-100 text-emerald-700";
  if (status === 2) return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}
